import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers,
    faMagnifyingGlass,
    faRotateRight,
    faPlus,
    faCalendar,
    faCheck,
    faChartBar,
    faLayerGroup,
    faExclamationTriangle,
    faCircleExclamation,
    faRobot,
    faSpinner,
    faCog
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';
import { workingGroupsService } from '../../services/workingGroupsService';
import LoadingSpinner from '../Common/LoadingSpinner';
import './WorkingGroupsManagement.css';
import WorkingGroupDetailsModal from './WorkingGroupDetailsModal';
import apiHelper from '../../utils/apiHelper';
import CreateWorkingGroupModal from './CreateWorkingGroupModal';
import WGParticipationModal from './WGParticipationModal';
import { removeURLs, cleanLinks, formatDateSimple } from '../../utils/textUtils';

// Add stripHtml function
const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

function WorkingGroupsManagement({ currentUser }) {
    const { user } = useAuth0();
    const [workingGroups, setWorkingGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        key: 'WorkingGroupName',
        direction: 'asc'
    });
    const itemsPerPage = 20;
    const [filters, setFilters] = useState({
        type: 'all',
        status: 'all',
        dateRange: 'all'
    });
    const [showDetails, setShowDetails] = useState(false);
    const [selectedWorkingGroup, setSelectedWorkingGroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [groupMembersMap, setGroupMembersMap] = useState({});
    const [isIndexing, setIsIndexing] = useState(false);
    const [showParticipationModal, setShowParticipationModal] = useState(false);
    const [isIndexingUpdates, setIsIndexingUpdates] = useState(false);
    
    // WG Debug state
    const [showWGDebugModal, setShowWGDebugModal] = useState(false);
    const [wgDebugResults, setWGDebugResults] = useState(null);
    const [isLoadingWGDebug, setIsLoadingWGDebug] = useState(false);
    const [showWGDocsModal, setShowWGDocsModal] = useState(false);
    const [wgDocsResults, setWGDocsResults] = useState(null);
    const [isLoadingWGDocs, setIsLoadingWGDocs] = useState(false);

    const fetchWorkingGroups = async () => {
        setIsRefreshing(true);
        try {
            const response = await workingGroupsService.getWorkingGroups();
            setWorkingGroups(response);
        } catch (error) {
            console.error('Error fetching working groups:', error);
            toast.error('Failed to fetch working groups');
        } finally {
            setIsRefreshing(false);
            setIsLoading(false);
        }
    };

    const refreshWorkingGroups = async () => {
        try {
            const data = await workingGroupsService.getWorkingGroups();
            setWorkingGroups(data);
        } catch (error) {
            console.error('Error refreshing working groups:', error);
            toast.error('Failed to refresh working groups');
        }
    };

    const fetchAllGroupMembers = async () => {
        try {
            const membersPromises = workingGroups.map(group => 
                workingGroupsService.getWorkingGroupMembers(group.WorkingGroupName)
                    .then(members => [group.WorkingGroupName, members])
                    .catch(error => {
                        console.error(`Error fetching members for ${group.WorkingGroupName}:`, error);
                        return [group.WorkingGroupName, []];
                    })
            );
            
            const results = await Promise.all(membersPromises);
            const membersMap = Object.fromEntries(results);
            setGroupMembersMap(membersMap);
        } catch (error) {
            console.error('Error fetching group members:', error);
        }
    };

    useEffect(() => {
        fetchWorkingGroups();
    }, []);

    useEffect(() => {
        if (workingGroups.length > 0) {
            fetchAllGroupMembers();
        }
    }, [workingGroups]);

    // Add stats calculation
    const getStats = () => {
        const stats = {
            total: workingGroups.length,
            active: workingGroups.filter(g => g.PublishFlag).length,
            inactive: workingGroups.filter(g => !g.PublishFlag).length,
            types: {},
            attentionPoints: {
                noMembers: workingGroups.filter(g => 
                    !groupMembersMap[g.WorkingGroupName] || 
                    groupMembersMap[g.WorkingGroupName].length === 0
                ).length,
                noChair: workingGroups.filter(g => {
                    const members = groupMembersMap[g.WorkingGroupName] || [];
                    return !members.some(m => m.fields?.WorkingGroupPosition === 'Chair');
                }).length,
                expiringSoon: workingGroups.filter(g => {
                    if (!g.EndDate0) return false;
                    const end = new Date(g.EndDate0);
                    const now = new Date();
                    const monthsUntilExpiry = (end.getFullYear() - now.getFullYear()) * 12 + 
                        (end.getMonth() - now.getMonth());
                    return monthsUntilExpiry <= 3 && monthsUntilExpiry > 0;
                }).length
            }
        };

        // Calculate type distribution
        workingGroups.forEach(group => {
            const type = group.WorkingGroupType || 'Unspecified';
            stats.types[type] = (stats.types[type] || 0) + 1;
        });

        return stats;
    };

    // Enhanced filtering
    const filteredGroups = workingGroups.filter(group => {
        const matchesSearch = group.WorkingGroupName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filters.type === 'all' || group.WorkingGroupType === filters.type;
        const matchesStatus = filters.status === 'all' || 
            (filters.status === 'active' ? group.PublishFlag : !group.PublishFlag);
        
        let matchesDate = true;
        if (filters.dateRange !== 'all') {
            const now = new Date();
            const start = new Date(group.StartDate0);
            switch (filters.dateRange) {
                case 'thisMonth':
                    matchesDate = start.getMonth() === now.getMonth() && 
                                start.getFullYear() === now.getFullYear();
                    break;
                case 'thisYear':
                    matchesDate = start.getFullYear() === now.getFullYear();
                    break;
                case 'active':
                    matchesDate = (!group.EndDate0 || new Date(group.EndDate0) > now) && 
                                new Date(group.StartDate0) <= now;
                    break;
            }
        }

        return matchesSearch && matchesType && matchesStatus && matchesDate;
    });

    const stats = getStats();

    // Sort groups
    const sortedGroups = [...filteredGroups].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        
        // Helper for null-safe string comparison
        const compareStrings = (valA, valB) => {
            return (valA || '').localeCompare(valB || '');
        };

        switch (sortConfig.key) {
            case 'WorkingGroupName':
                return direction * compareStrings(a.WorkingGroupName, b.WorkingGroupName);
            
            case 'WorkingGroupType':
                return direction * compareStrings(a.WorkingGroupType, b.WorkingGroupType);
            
            case 'SteeringGroupName':
                return direction * compareStrings(a.SteeringGroupName, b.SteeringGroupName);
            
            case 'StartDate':
                return direction * (new Date(a.StartDate0 || 0) - new Date(b.StartDate0 || 0));
            
            case 'EndDate':
                return direction * (new Date(a.EndDate0 || 0) - new Date(b.EndDate0 || 0));
            
            case 'Status':
                // Sort by PublishFlag (true values first)
                return direction * (Number(b.PublishFlag) - Number(a.PublishFlag));
                
            default:
                return 0;
        }
    });

    // Pagination
    const paginatedGroups = sortedGroups.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const totalPages = Math.ceil(sortedGroups.length / itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key 
                ? current.direction === 'asc' ? 'desc' : 'asc'
                : 'asc'
        }));
    };

    const SortIndicator = ({ column }) => {
        if (sortConfig.key !== column) return null;
        return (
            <span className="sort-indicator">
                {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
            </span>
        );
    };

    const handleWorkingGroupClick = (workingGroup) => {
        setSelectedWorkingGroup(workingGroup);
    };

    const handleMembersTabClick = async (workingGroup) => {
        try {
            const membersData = await apiHelper.getWorkingGroupMembers(workingGroup.WorkingGroupName);
            setMembers(membersData);
            setSelectedWorkingGroup(workingGroup);
        } catch (error) {
            console.error('Error fetching members:', error);
            toast.error('Failed to fetch members');
        }
    };

    // Add the bulk indexing handler
    const handleBulkIndex = async () => {
        if (!window.confirm('Are you sure you want to index all working groups?')) {
            return;
        }

        if (!workingGroups || workingGroups.length === 0) {
            toast.error('No working groups found to index');
            return;
        }

        setIsIndexing(true);
        let successCount = 0;
        let errorCount = 0;
        let skippedCount = 0;
        const BATCH_SIZE = 5;
        const errors = [];

        // Create a persistent toast for overall progress
        const progressToastId = toast.loading(
            `Starting indexing of ${workingGroups.length} working groups...`, 
            { autoClose: false }
        );

        try {
            // Split groups into batches
            for (let i = 0; i < workingGroups.length; i += BATCH_SIZE) {
                const batch = workingGroups.slice(i, i + BATCH_SIZE);
                const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
                const totalBatches = Math.ceil(workingGroups.length / BATCH_SIZE);
                
                // Update batch progress
                toast.update(progressToastId, {
                    render: `Processing batch ${batchNumber}/${totalBatches} (${i} of ${workingGroups.length} groups)`,
                });
                
                // Process each group in the current batch
                for (const group of batch) {
                    try {

                        
                        // Fetch additional data for the group
                        const [members, updates, torData] = await Promise.all([
                            workingGroupsService.getWorkingGroupMembers(group.WorkingGroupName),
                            workingGroupsService.getWorkingGroupUpdates(group.id),
                            workingGroupsService.getWorkingGroupTOR(group.id)
                        ]);

                        // Format dates
                        const startDate = group.StartDate0 ? new Date(group.StartDate0).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                        }) : '';
                        
                        const endDate = group.EndDate0 ? new Date(group.EndDate0).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                        }) : '';

                        const monthYear = group.StartDate0 ? 
                            new Date(group.StartDate0).toLocaleString('default', { month: 'long' }) + ' ' + 
                            new Date(group.StartDate0).getFullYear() : '';

                        // Get TOR data
                        const torHeader = torData.find(tor => tor.fields?.Category === "Header")?.fields?.TORDescription || '';
                        const torObjectives = torData.filter(tor => tor.fields?.Category === "Objective")
                            .map(obj => obj.fields?.TORDescription || '');

                        // Create structured content
                        const workingGroupText = `[Type: Working Group]
[Title: ${group.WorkingGroupName}]
[WorkingGroupType: ${group.WorkingGroupType}]
[SteeringGroup: ${group.SteeringGroupName}]
[StartDate: ${startDate}]
[EndDate: ${endDate}]
[MonthYear: ${monthYear}]

[Summary]
This is a ${group.WorkingGroupType} working group focused on ${group.WorkingGroupName}. 
${group.SteeringGroupName !== 'N/a' ? `It is part of the ${group.SteeringGroupName} steering group.` : ''}
The group is active from ${startDate} to ${endDate}.

${(torHeader || torObjectives.length > 0) ? `[Terms of Reference]
${torHeader ? `${stripHtml(torHeader)}` : ''}` : ''}

${torObjectives.length > 0 ? 'Objectives:' : ''}
${torObjectives.map((obj, index) => `${index + 1}. ${stripHtml(obj)}`).join('\n')}


${updates.length > 0 ? 'Updates:' : ''}
${updates.map(update => {
    const content = update.update || update.Update_x003a_Nov24 || 'No content available';
    const withoutHtml = stripHtml(content);
    const cleanedContent = removeURLs(cleanLinks(withoutHtml));
    return `Date: ${formatDateSimple(update.updateDate) || 'N/A'}.
${cleanedContent}.
By: ${update.updatedBy || 'Unknown'}.`;
}).join('\n\n')}`;

                        // Index into Qdrant
                        const metadata = {
                            title: group.WorkingGroupName,
                            type: 'Working Group',
                            workingGroupType: group.WorkingGroupType,
                            steeringGroup: group.SteeringGroupName,
                            startDate: group.StartDate0,
                            endDate: group.EndDate0,
                            updateCount: updates.length,
                            memberCount: members.length,
                            hasTOR: Boolean(torHeader || torObjectives.length > 0),
                            torObjectiveCount: torObjectives.length,
                            listItemId: group.id,
                            text: workingGroupText,
                            content: workingGroupText
                        };

                        await apiHelper.indexQdrantDocument(workingGroupText, metadata, group.id);
                        successCount++;

                        // Update progress toast after each successful index
                        toast.update(progressToastId, {
                            render: `Processed ${successCount + errorCount + skippedCount} of ${workingGroups.length} groups\nSuccess: ${successCount} | Errors: ${errorCount} | Skipped: ${skippedCount}`,
                        });

                    } catch (error) {
                        console.error(`Error processing working group ${group.WorkingGroupName}:`, error);
                        errorCount++;
                        errors.push({
                            id: group.id,
                            name: group.WorkingGroupName,
                            error: error.message
                        });
                    }
                }

                // Pause between batches with progress update
                if (i + BATCH_SIZE < workingGroups.length) {
                    toast.update(progressToastId, {
                        render: `Completed batch ${batchNumber}/${totalBatches}. Pausing briefly...`,
                    });
                    await new Promise(resolve => setTimeout(resolve, 3000));
                }
            }

            // Final summary
            toast.dismiss(progressToastId);
            
            if (successCount > 0) {
                toast.success(
                    `Indexing Complete:\n` +
                    `✅ Successfully indexed: ${successCount}\n` +
                    `Failed: ${errorCount}\n` +
                    `⏭️ Skipped: ${skippedCount}\n` +
                    `Check console for detailed error log`
                );
                await fetchWorkingGroups();
            } else {
                toast.error('No working groups were successfully indexed');
            }
        } catch (error) {
            console.error('Error in bulk indexing process:', error);
            toast.error(error.message || 'Failed to index working groups');
        } finally {
            setIsIndexing(false);
            // Save error log
            localStorage.setItem('lastWorkingGroupIndexingErrors', JSON.stringify({
                timestamp: new Date().toISOString(),
                total: workingGroups.length,
                success: successCount,
                errors: errorCount,
                skipped: skippedCount,
                errorDetails: errors
            }));
        }
    };

    // Handler for indexing WG periodic updates
    const handleIndexWGUpdates = async () => {
        if (!window.confirm('This will index all Working Group periodic updates into Qdrant. Continue?')) {
            return;
        }

        setIsIndexingUpdates(true);
        const progressToastId = toast.loading('Indexing WG periodic updates...', { autoClose: false });

        try {
            const response = await apiHelper.indexWGPeriodicUpdates();
            
            toast.dismiss(progressToastId);
            
            if (response.success) {
                toast.success(
                    `✅ WG Updates Indexed!\n` +
                    `Total: ${response.total}\n` +
                    `Successfully indexed: ${response.indexed}\n` +
                    `Errors: ${response.errors}\n` +
                    `Duration: ${(response.duration / 1000).toFixed(1)}s`,
                    { autoClose: 5000 }
                );
            } else {
                toast.error(`Failed to index WG updates: ${response.error || 'Unknown error'}`);
            }
        } catch (error) {
            toast.dismiss(progressToastId);
            console.error('Error indexing WG updates:', error);
            toast.error(`Error: ${error.message || 'Failed to index WG updates'}`);
        } finally {
            setIsIndexingUpdates(false);
        }
    };

    // Debug WG Updates in Qdrant
    const debugWGUpdates = async () => {
        setIsLoadingWGDebug(true);
        setShowWGDebugModal(true);
        setWGDebugResults(null);
        
        try {
            const response = await apiHelper.get('/api/qdrant/debug-wg-updates');
            setWGDebugResults(response.data);
        } catch (error) {
            console.error('Error debugging WG updates:', error);
            setWGDebugResults({
                success: false,
                error: error.message || 'Failed to fetch debug information'
            });
        } finally {
            setIsLoadingWGDebug(false);
        }
    };

    // Debug WG Documents in Qdrant
    const debugWGDocuments = async () => {
        setIsLoadingWGDocs(true);
        setShowWGDocsModal(true);
        setWGDocsResults(null);
        
        try {
            const response = await apiHelper.get('/api/qdrant/debug-wg-documents');
            setWGDocsResults(response.data);
        } catch (error) {
            console.error('Error debugging WG documents:', error);
            setWGDocsResults({
                success: false,
                error: error.message || 'Failed to fetch debug information'
            });
        } finally {
            setIsLoadingWGDocs(false);
        }
    };

    return (
        <div className='working-groups-management'>
            {isLoading ? (
                <div className="loading-container">
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    <div className="wg-controls">
                        <div className="button-group">
                            <button 
                                className="control-btn primary"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Add Group
                            </button>
                            <button 
                                className="control-btn secondary"
                                onClick={fetchWorkingGroups}
                                disabled={isRefreshing}
                            >
                                <FontAwesomeIcon 
                                    icon={faRotateRight} 
                                    spin={isRefreshing}
                                /> 
                                Refresh
                            </button>
                            {/* <button 
                                className="control-btn secondary"
                                onClick={handleBulkIndex}
                                disabled={isIndexing}
                            >
                                <FontAwesomeIcon 
                                    icon={isIndexing ? faSpinner : faRobot} 
                                    spin={isIndexing}
                                /> 
                                {isIndexing ? 'Indexing...' : 'Index All'}
                            </button> */}
                            <button 
                                className="control-btn secondary"
                                onClick={handleIndexWGUpdates}
                                disabled={isIndexingUpdates}
                                title="Index all WG periodic updates into Qdrant for AI search"
                            >
                                <FontAwesomeIcon 
                                    icon={isIndexingUpdates ? faSpinner : faRobot} 
                                    spin={isIndexingUpdates}
                                /> 
                                {isIndexingUpdates ? 'Indexing Updates...' : 'Index WG Updates'}
                            </button>
                            <button 
                                className="control-btn secondary"
                                onClick={debugWGUpdates}
                                disabled={isLoadingWGDebug}
                                title="Check what WG Updates are indexed in Qdrant"
                                style={{
                                    background: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                                    color: 'white'
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isLoadingWGDebug ? faSpinner : faExclamationTriangle} 
                                    spin={isLoadingWGDebug}
                                /> 
                                {isLoadingWGDebug ? 'Checking...' : '🔍 Debug Updates'}
                            </button>
                            <button 
                                className="control-btn secondary"
                                onClick={debugWGDocuments}
                                disabled={isLoadingWGDocs}
                                title="Check what WG Documents are indexed in Qdrant"
                                style={{
                                    background: 'linear-gradient(135deg, #e67e22, #d35400)',
                                    color: 'white'
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isLoadingWGDocs ? faSpinner : faExclamationTriangle} 
                                    spin={isLoadingWGDocs}
                                /> 
                                {isLoadingWGDocs ? 'Checking...' : '📄 Debug Docs'}
                            </button>
                            <button 
                                className="control-btn secondary"
                                onClick={() => setShowParticipationModal(true)}
                            >
                                <FontAwesomeIcon icon={faCog} /> 
                                Manage Stats
                            </button>
                        </div>
                    </div>

                    <div className="wg-stats">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                            <div className="stat-details">
                                <h3>Total Groups</h3>
                                <p className="stat-number">{stats.total}</p>
                                <p className="stat-breakdown">
                                    Active: {stats.active} | Inactive: {stats.inactive}
                                </p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <FontAwesomeIcon icon={faLayerGroup} />
                            </div>
                            <div className="stat-details">
                                <h3>Group Types</h3>
                                <div className="type-distribution">
                                    {Object.entries(stats.types).map(([type, count]) => (
                                        <div key={type} className="type-item">
                                            <span className="type-label">{type}</span>
                                            <span className="type-count">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* <div className="stat-card attention-card">
                            <div className="stat-icon">
                                <FontAwesomeIcon icon={faCircleExclamation} className="attention-icon" />
                            </div>
                            <div className="stat-details">
                                <h3>Needs Attention 🎪</h3>
                                <div className="attention-points">
                                    {stats.attentionPoints.noMembers > 0 && (
                                        <div className="attention-item">
                                            <span className="attention-count">{stats.attentionPoints.noMembers}</span>
                                            <span className="attention-label">Groups with No Members</span>
                                        </div>
                                    )}
                                    {stats.attentionPoints.noChair > 0 && (
                                        <div className="attention-item">
                                            <span className="attention-count">{stats.attentionPoints.noChair}</span>
                                            <span className="attention-label">Groups without Chair</span>
                                        </div>
                                    )}
                                    {stats.attentionPoints.expiringSoon > 0 && (
                                        <div className="attention-item">
                                            <span className="attention-count">{stats.attentionPoints.expiringSoon}</span>
                                            <span className="attention-label">Groups Expiring Soon</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div> */}
                    </div>

                    <div className="content-toolbar">
                        <div className="toolbar-section search-section">
                            <div className="filters-row">
                                <div className="search-box">
                                    <input
                                        type="text"
                                        placeholder="Search groups..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
                                </div>
                                <div className="filter-selects">
                                    <select
                                        value={filters.type}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            type: e.target.value
                                        }))}
                                    >
                                        <option value="all">All Types</option>
                                        {Object.keys(stats.types).map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            status: e.target.value
                                        }))}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="wg-table">
                        <table>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('WorkingGroupName')} className="sortable">
                                        Working Group Name <SortIndicator column="WorkingGroupName" />
                                    </th>
                                    <th onClick={() => handleSort('WorkingGroupType')} className="sortable">
                                        Type <SortIndicator column="WorkingGroupType" />
                                    </th>
                                    <th onClick={() => handleSort('SteeringGroupName')} className="sortable">
                                        Steering Group <SortIndicator column="SteeringGroupName" />
                                    </th>
                                    <th onClick={() => handleSort('StartDate')} className="sortable">
                                        Start Date <SortIndicator column="StartDate" />
                                    </th>
                                    <th onClick={() => handleSort('EndDate')} className="sortable">
                                        End Date <SortIndicator column="EndDate" />
                                    </th>
                                    <th onClick={() => handleSort('Status')} className="sortable">
                                        Status <SortIndicator column="Status" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedGroups.map((group, index) => (
                                    <tr 
                                        key={index}
                                        onClick={() => {
                                            handleWorkingGroupClick(group);
                                            setShowDetails(true);
                                        }}
                                    >
                                        <td className="group-name-cell">{group.WorkingGroupName}</td>
                                        <td>{group.WorkingGroupType || '-'}</td>
                                        <td>{group.SteeringGroupName || '-'}</td>
                                        <td>
                                            <span className="date-cell">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {group.StartDate0 ? new Date(group.StartDate0).getFullYear() : '-'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="date-cell">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {group.EndDate0 ? new Date(group.EndDate0).getFullYear() : '-'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${group.PublishFlag ? 'active' : 'inactive'}`}>
                                                {group.PublishFlag ? (
                                                    <><FontAwesomeIcon icon={faCheck} /> Active</>
                                                ) : 'Inactive'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <button 
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    Previous
                                </button>
                                <span className="pagination-info">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button 
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>

                    {showDetails && selectedWorkingGroup && (
                        <WorkingGroupDetailsModal
                            workingGroup={selectedWorkingGroup}
                            show={showDetails}
                            onHide={() => setShowDetails(false)}
                            onUpdate={refreshWorkingGroups}
                        />
                    )}

                    <CreateWorkingGroupModal
                        show={showCreateModal}
                        onHide={() => setShowCreateModal(false)}
                        onCreated={fetchWorkingGroups}
                    />

                    <WGParticipationModal
                        show={showParticipationModal}
                        onHide={() => setShowParticipationModal(false)}
                    />

                    {/* WG Updates Debug Modal */}
                    {showWGDebugModal && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                maxWidth: '900px',
                                width: '90%',
                                maxHeight: '80vh',
                                overflow: 'auto',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                            }}>
                                <div style={{
                                    padding: '24px',
                                    borderBottom: '2px solid #f0f0f0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                                    color: 'white',
                                    borderTopLeftRadius: '12px',
                                    borderTopRightRadius: '12px'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                        🔍 Working Group Updates Debug
                                    </h3>
                                    <button 
                                        onClick={() => setShowWGDebugModal(false)}
                                        style={{
                                            background: 'rgba(255,255,255,0.2)',
                                            border: 'none',
                                            color: 'white',
                                            fontSize: '24px',
                                            cursor: 'pointer',
                                            padding: '4px 12px',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    {isLoadingWGDebug ? (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#9b59b6' }} />
                                            <p style={{ marginTop: '16px', color: '#666' }}>Checking Qdrant...</p>
                                        </div>
                                    ) : wgDebugResults ? (
                                        wgDebugResults.success ? (
                                            <>
                                                <div style={{
                                                    background: wgDebugResults.totalFound > 0 ? 'linear-gradient(135deg, #d4edda, #c3e6cb)' : 'linear-gradient(135deg, #f8d7da, #f5c6cb)',
                                                    padding: '20px',
                                                    borderRadius: '8px',
                                                    marginBottom: '24px',
                                                    border: wgDebugResults.totalFound > 0 ? '2px solid #28a745' : '2px solid #dc3545'
                                                }}>
                                                    <h4 style={{ margin: '0 0 12px 0', color: wgDebugResults.totalFound > 0 ? '#155724' : '#721c24' }}>
                                                        {wgDebugResults.totalFound > 0 ? '✅ ' : '❌ '}
                                                        Found {wgDebugResults.totalFound} WG Update{wgDebugResults.totalFound !== 1 ? 's' : ''}
                                                    </h4>
                                                    <p style={{ margin: '4px 0', color: wgDebugResults.totalFound > 0 ? '#155724' : '#721c24', fontSize: '14px' }}>
                                                        <strong>Collection:</strong> {wgDebugResults.collection}
                                                    </p>
                                                </div>
                                                {wgDebugResults.totalFound > 0 && (
                                                    <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                                                        {wgDebugResults.documents.map((doc, index) => (
                                                            <div key={index} style={{
                                                                padding: '16px',
                                                                borderBottom: index < wgDebugResults.documents.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                                                            }}>
                                                                <div style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>{doc.title}</div>
                                                                <div style={{ fontSize: '13px', color: '#666', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                                    <div><strong>WG:</strong> {doc.workingGroup || 'N/A'}</div>
                                                                    <div><strong>Date:</strong> {doc.updateDate ? new Date(doc.updateDate).toLocaleDateString('en-GB') : 'N/A'}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{ padding: '40px', textAlign: 'center', background: '#f8d7da', borderRadius: '8px', border: '2px solid #dc3545' }}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545' }} />
                                                <h4 style={{ marginTop: '16px', color: '#721c24' }}>Error</h4>
                                                <p style={{ color: '#721c24', marginTop: '8px' }}>{wgDebugResults.error}</p>
                                            </div>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* WG Documents Debug Modal */}
                    {showWGDocsModal && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                maxWidth: '900px',
                                width: '90%',
                                maxHeight: '80vh',
                                overflow: 'auto',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                            }}>
                                <div style={{
                                    padding: '24px',
                                    borderBottom: '2px solid #f0f0f0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: 'linear-gradient(135deg, #e67e22, #d35400)',
                                    color: 'white',
                                    borderTopLeftRadius: '12px',
                                    borderTopRightRadius: '12px'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                        📄 Working Group Documents Debug
                                    </h3>
                                    <button 
                                        onClick={() => setShowWGDocsModal(false)}
                                        style={{
                                            background: 'rgba(255,255,255,0.2)',
                                            border: 'none',
                                            color: 'white',
                                            fontSize: '24px',
                                            cursor: 'pointer',
                                            padding: '4px 12px',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    {isLoadingWGDocs ? (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#e67e22' }} />
                                            <p style={{ marginTop: '16px', color: '#666' }}>Checking Qdrant...</p>
                                        </div>
                                    ) : wgDocsResults ? (
                                        wgDocsResults.success ? (
                                            <>
                                                <div style={{
                                                    background: wgDocsResults.totalFound > 0 ? 'linear-gradient(135deg, #d4edda, #c3e6cb)' : 'linear-gradient(135deg, #f8d7da, #f5c6cb)',
                                                    padding: '20px',
                                                    borderRadius: '8px',
                                                    marginBottom: '24px',
                                                    border: wgDocsResults.totalFound > 0 ? '2px solid #28a745' : '2px solid #dc3545'
                                                }}>
                                                    <h4 style={{ margin: '0 0 12px 0', color: wgDocsResults.totalFound > 0 ? '#155724' : '#721c24' }}>
                                                        {wgDocsResults.totalFound > 0 ? '✅ ' : '❌ '}
                                                        Found {wgDocsResults.totalFound} WG Document{wgDocsResults.totalFound !== 1 ? 's' : ''}
                                                    </h4>
                                                    <p style={{ margin: '4px 0', color: wgDocsResults.totalFound > 0 ? '#155724' : '#721c24', fontSize: '14px' }}>
                                                        <strong>Collection:</strong> {wgDocsResults.collection}
                                                    </p>
                                                </div>
                                                {wgDocsResults.totalFound > 0 ? (
                                                    <>
                                                        {wgDocsResults.samplePayload && (
                                                            <div style={{ 
                                                                marginBottom: '20px', 
                                                                padding: '16px', 
                                                                background: '#fff3cd', 
                                                                border: '1px solid #ffc107',
                                                                borderRadius: '8px'
                                                            }}>
                                                                <h5 style={{ margin: '0 0 12px 0', color: '#856404' }}>
                                                                    🔍 Sample Document Payload (First Doc):
                                                                </h5>
                                                                <pre style={{ 
                                                                    background: '#fff', 
                                                                    padding: '12px', 
                                                                    borderRadius: '4px',
                                                                    fontSize: '11px',
                                                                    maxHeight: '300px',
                                                                    overflow: 'auto',
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-all'
                                                                }}>
                                                                    {JSON.stringify(wgDocsResults.samplePayload, null, 2)}
                                                                </pre>
                                                            </div>
                                                        )}
                                                        <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                                                            {wgDocsResults.documents.map((doc, index) => (
                                                                <div key={index} style={{
                                                                    padding: '16px',
                                                                    borderBottom: index < wgDocsResults.documents.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                                                                }}>
                                                                    <div style={{ fontWeight: '600', color: '#333', marginBottom: '8px' }}>{doc.title}</div>
                                                                    <div style={{ fontSize: '13px', color: '#666', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                                        <div><strong>WG:</strong> {doc.title || 'N/A'}</div>
                                                                        <div><strong>Year:</strong> {doc.publishDate ? new Date(doc.publishDate).getFullYear() : 'N/A'}</div>
                                                                    </div>
                                                                    {doc.allFields && (
                                                                        <div style={{ 
                                                                            marginTop: '8px', 
                                                                            fontSize: '11px', 
                                                                            color: '#999',
                                                                            fontFamily: 'monospace'
                                                                        }}>
                                                                            <strong>Available Fields:</strong> {doc.allFields}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '8px' }}>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545' }} />
                                                        <h4 style={{ marginTop: '16px', color: '#721c24' }}>⚠️ No WG Documents Found!</h4>
                                                        <p style={{ color: '#721c24', marginTop: '8px' }}>
                                                            Regular WG documents (meeting records) are missing. Run a complete rebuild to restore them.
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{ padding: '40px', textAlign: 'center', background: '#f8d7da', borderRadius: '8px', border: '2px solid #dc3545' }}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545' }} />
                                                <h4 style={{ marginTop: '16px', color: '#721c24' }}>Error</h4>
                                                <p style={{ color: '#721c24', marginTop: '8px' }}>{wgDocsResults.error}</p>
                                            </div>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}
                
                </>
            )}
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="light"
            />
        </div>
    );
}

WorkingGroupsManagement.propTypes = {
    currentUser: PropTypes.object
};

export default WorkingGroupsManagement; 